/**
 * Eksport siatki MES do pliku tekstowego (.pddm) dla symulatora C++.
 *
 * Format PDD_MESH_V1 – opis sekcji:
 *   WIDTH_MM / HEIGHT_MM / THICKNESS_MM  – wymiary arkusza [mm]
 *
 *   VERTICES n
 *   idx  x_mm  y_mm  fixed
 *     idx   – indeks wierzchołka (0-based)
 *     x,y   – współrzędne w mm (środek arkusza = 0,0)
 *     fixed – 1 = nieruchomy (należy do obszaru materiału), 0 = ruchomy (wnętrze otworu)
 *
 *   TRIANGLES m
 *   v1  v2  v3  label
 *     v1..v3 – indeksy wierzchołków (0-based, CCW)
 *     label  – 0 = trójkąt materiałowy, N>0 = trójkąt wewnątrz otworu N
 *
 *   EDGES k
 *   v1  v2
 *     Wszystkie unikalne krawędzie siatki (v1 < v2).
 *     Krawędź wewnętrzna (count=2): kandydat na zawias sztywności na zginanie.
 *     Krawędź graniczna (count=1): krawędź brzegowa arkusza lub otworu.
 *
 * Plik jest czytelny dla człowieka i łatwy do parsowania w C++ (scanf / istringstream).
 * Linie zaczynające się od '#' są komentarzami – można je ignorować przy parsowaniu.
 */

import { AppState, TriangulationResult } from '../types';

export function exportMeshAsPddm(
  tri:      TriangulationResult,
  appState: AppState,
): void {
  const { vertices, triangles, triLabels } = tri;
  const n = vertices.length;
  const W = appState.materialWidth;
  const H = appState.materialHeight;
  const T = appState.thicknessMm;

  // ── isFixed: wierzchołek jest nieruchomy gdy należy do ≥1 trójkąta materiałowego ──
  const isFixed = new Uint8Array(n);
  for (let t = 0; t < triangles.length; t++) {
    if ((triLabels[t] ?? 0) === 0) {
      const [a, b, c] = triangles[t];
      isFixed[a] = isFixed[b] = isFixed[c] = 1;
    }
  }

  // ── Unikalne krawędzie (v1 < v2) ──
  const edgeSet = new Set<string>();
  for (const [a, b, c] of triangles) {
    const pairs: [number, number][] = [
      [Math.min(a,b), Math.max(a,b)],
      [Math.min(b,c), Math.max(b,c)],
      [Math.min(c,a), Math.max(c,a)],
    ];
    for (const [u, v] of pairs) edgeSet.add(`${u} ${v}`);
  }
  const edges = Array.from(edgeSet);

  // ── Budowanie pliku ──
  const lines: string[] = [];

  lines.push('PDD_MESH_V1');
  lines.push(`WIDTH_MM ${W.toFixed(4)}`);
  lines.push(`HEIGHT_MM ${H.toFixed(4)}`);
  lines.push(`THICKNESS_MM ${T.toFixed(4)}`);
  lines.push('');

  lines.push(`VERTICES ${n}`);
  lines.push('# idx  x_mm  y_mm  fixed(0=ruchomy 1=nieruchomy)');
  for (let i = 0; i < n; i++) {
    const { x, y } = vertices[i];
    lines.push(`${i}  ${x.toFixed(4)}  ${y.toFixed(4)}  ${isFixed[i]}`);
  }
  lines.push('');

  lines.push(`TRIANGLES ${triangles.length}`);
  lines.push('# v1  v2  v3  label(0=material N=opening_N)');
  for (let t = 0; t < triangles.length; t++) {
    const [a, b, c] = triangles[t];
    lines.push(`${a}  ${b}  ${c}  ${triLabels[t] ?? 0}`);
  }
  lines.push('');

  lines.push(`EDGES ${edges.length}`);
  lines.push('# v1  v2  (v1<v2, all unique edges)');
  for (const e of edges) lines.push(e);

  // ── Pobieranie pliku ──
  const content = lines.join('\n') + '\n';
  const blob = new Blob([content], { type: 'text/plain' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'pdd_mesh.pddm';
  a.click();
  URL.revokeObjectURL(url);
}
